import type Phaser from "phaser";

/**
 * The props a view withholds (ADR-142 decision 3.5): ids whose images stay hidden, with the ease that hides or shows
 * one. A prop in the set is hidden whenever its image is placed, so it stays hidden when its chunk leaves and enters
 * again. With a fade the image eases to clear before it is hidden, or from clear when it comes back; a scene with no
 * tweens, or a fade of 0, changes it at once.
 */
export class PropWithholding {
  private readonly ids = new Set<string>();
  private readonly fades = new Map<string, Phaser.Tweens.Tween>();

  /**
   * @param scene The scene whose tweens ease an image.
   */
  public constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Whether a prop is withheld.
   *
   * @param id The prop's id.
   * @returns True while it is.
   */
  public has(id: string): boolean {
    return this.ids.has(id);
  }

  /**
   * Whether an ease runs on a prop's image.
   *
   * @param id The prop's id.
   * @returns True while one does.
   */
  public fading(id: string): boolean {
    return this.fades.has(id);
  }

  /**
   * Stops the ease of a prop, if one runs.
   *
   * @param id The prop's id.
   */
  public stop(id: string): void {
    const fade = this.fades.get(id);
    if (fade !== undefined) {
      fade.stop();
      this.fades.delete(id);
    }
  }

  /**
   * Withholds a prop or draws it again, easing its image when it has one.
   *
   * @param id The prop's id.
   * @param withheld True to hide it.
   * @param fadeMs The ease's length in milliseconds.
   * @param image The prop's image, or null when the view does not draw it.
   * @param rest The alpha the image rests at.
   * @returns Whether the set changed.
   */
  public set(id: string, withheld: boolean, fadeMs: number, image: Phaser.GameObjects.Image | null, rest: number): boolean {
    if (withheld === this.ids.has(id)) {
      return false;
    }
    if (withheld) {
      this.ids.add(id);
    } else {
      this.ids.delete(id);
    }
    if (image === null) {
      return false;
    }
    this.stop(id);
    const tweens = this.scene.tweens as Phaser.Tweens.TweenManager | undefined;
    if (fadeMs <= 0 || tweens === undefined) {
      image.setVisible(!withheld);
      image.setAlpha(rest);
      return true;
    }
    if (!withheld) {
      image.setAlpha(0);
      image.setVisible(true);
    }
    this.fades.set(
      id,
      tweens.add({
        targets: image,
        alpha: withheld ? 0 : rest,
        duration: fadeMs,
        onComplete: () => {
          this.fades.delete(id);
          if (withheld) {
            image.setVisible(false);
            image.setAlpha(rest);
          }
        },
      }),
    );
    return true;
  }
}
